/**
 * Builds a `rowKey` resolver for `SortableTable` that derives a stable key
 * from row content instead of the render index. Index-based keys break both
 * React reconciliation and the selection `Set` when the table re-sorts: the
 * indices get remapped, so selection sticks to whatever row landed on the
 * selected position.
 *
 * The key is the JSON serialisation of the row (extraction event rows have no
 * id field, and any hand-picked subset of fields can collide), with a counter
 * suffix appended only for truly identical rows. The counter is assigned in
 * source-array order, so keys stay stable regardless of the sort the table
 * applies — identical rows are interchangeable anyway.
 */
export function createStableRowKey<T extends object>(rows: T[]): (row: T, index: number) => string {
  const keyByRow = new Map<T, string>();
  const countByKey = new Map<string, number>();
  for (const row of rows) {
    const base = JSON.stringify(row);
    const count = countByKey.get(base) ?? 0;
    countByKey.set(base, count + 1);
    keyByRow.set(row, count === 0 ? base : `${base}~${count}`);
  }
  // Fallback covers rows not present when the resolver was built (shouldn't
  // happen — SortableTable sorts the same row references it receives).
  return (row, index) => keyByRow.get(row) ?? `${JSON.stringify(row)}~fallback-${index}`;
}
